import { useNavigate, useParams } from 'react-router';
import { ScreenSkeleton } from '../../../../components/atoms/ScreenSkeleton';
import { ObservableLevelsEnum, PartName } from '../../../../data/enums';
import { CompleteButton } from '../../../../components/atoms/Button';
import { useEffect, useState } from 'react';
import { ScreenHeading } from '../../../../components/molecules/ScreenHeading';
import DropdownField from '../../../../components/atoms/DropdownField';
import { CheckboxWithComponents } from '../../../../components/atoms/CheckboxWithComponentsField';
import { ActionStatusBar } from '../../../../components/molecules/ActionStatusBar';
import TapeMeasure from '../../../../components/atoms/TapeMeasureWornLength';
import { PitchGaugeWornLengthComponent } from '../../../../components/atoms/PitchGaugeWornLength';
import { ScreenObservationLayout } from '../../../../components/templates/ScreenObservationLayout';
import { Label } from '../../../../components/atoms/Label';
import PhotoSelector from '../../../../components/atoms/PhotoSelector';
import { RichTextField } from '../../../../components/atoms/RichTextField';
import { ObservableItemIdentifier } from '../../../../api-contracts/other/ObservableItemIdentifier';
import { MdHelpers } from '../../../../helpers/mdHelpers';
import { ErrorBanner } from '../../../../components/atoms/ErrorBanner';
import { ObservationDraftScope } from '../../../../components/ObservationDraftScope';
import useInspectionCategoryType from '../../../../hooks/useInspectionCategoryType';
import { useInspectionCached } from '../../../../hooks/useInspectionCached';
import { useAssetEquipmentPartCached } from '../../../../hooks/useAssetEquipmentPartCached';

type PitchExtensionWearIndicatorField = {
  label: string;
  path: string;
};

type PitchExtensionWearIndicatorGroup = {
  label: string;
  fields: PitchExtensionWearIndicatorField[];
};

// FIXME: need to update component logic to use ObservableItemIdentifier
const wearIndicators: PitchExtensionWearIndicatorGroup[] = [
  {
    label: 'Pins',
    fields: [
      {
        label: 'Broken',
        path: 'Undercarriage.Shoes.PitchExtension.WearIndicators.Pins.Broken',
      },
      {
        label: 'Check tension',
        path: 'Undercarriage.Shoes.PitchExtension.WearIndicators.Pins.CheckTension',
      },
      {
        label: 'Visible Gap',
        path: 'Undercarriage.Shoes.PitchExtension.WearIndicators.Pins.VisibleGap',
      },
    ],
  },
  {
    label: 'BUSHES',
    fields: [
      {
        label: 'Cracking',
        path: 'Undercarriage.Shoes.PitchExtension.WearIndicators.Bushings.Cracking',
      },
      {
        label: 'Walking',
        path: 'Undercarriage.Shoes.PitchExtension.WearIndicators.Bushings.Walking',
      },
      {
        label: 'Loss',
        path: 'Undercarriage.Shoes.PitchExtension.WearIndicators.Bushings.Loss',
      },
    ],
  },
  {
    label: 'PIN LUGS',
    fields: [
      {
        label: 'Cracking',
        path: 'Undercarriage.Shoes.PitchExtension.WearIndicators.PinBoreLugs.Cracking',
      },
      {
        label: 'Broken',
        path: 'Undercarriage.Shoes.PitchExtension.WearIndicators.PinBoreLugs.Broken',
      },
    ],
  },
  {
    label: 'HARDWARE',
    fields: [
      {
        label: 'Loose',
        path: 'Undercarriage.Shoes.PitchExtension.WearIndicators.Hardware.Loose',
      },
      {
        label: 'Damaged',
        path: 'Undercarriage.Shoes.PitchExtension.WearIndicators.Hardware.Damage',
      },
      {
        label: 'Missing',
        path: 'Undercarriage.Shoes.PitchExtension.WearIndicators.Hardware.Missing',
      },
    ],
  },
];

export const PitchExtensionScreen = () => {
  const { externalId, sideName, partCategory } = useParams();
  const navigate = useNavigate();

  const { inspection } = useInspectionCached(externalId);

  const undercarriageCategory = useInspectionCategoryType({
    inspection,
  });

  const {
    equipmentPart,
    error: errorEquipmentPart,
    numberOfShoes,
  } = useAssetEquipmentPartCached({
    externalId,
    asset: inspection?.asset,
    sideName,
    partCategory,
    partName: PartName.pitchExtension,
  });

  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setError(null); // reset error
    async function getEquipment() {
      try {
        if (errorEquipmentPart) {
          setError(errorEquipmentPart);
          return;
        }
      } catch (error) {
        console.error('Error fetching equipment', error);
        setError('Error fetching equipment');
      }
    }

    getEquipment();
  }, [errorEquipmentPart, equipmentPart]);

  if (
    !equipmentPart ||
    !numberOfShoes ||
    !undercarriageCategory ||
    !externalId
  ) {
    return <ScreenSkeleton fields={4} />;
  }

  if (error) {
    return <ErrorBanner message={error} />;
  }

  const { Undercarriage } = ObservableItemIdentifier.Identifiers;

  return (
    <ObservationDraftScope inspectionId={externalId}>
      <div>
        <ScreenHeading
          eyebrow={'Shoes'}
          title={'Pitch extension'}
          partNumber={equipmentPart.part.partNumber}
          tooltipKey={'undercarriage-shoes-pitch-extension-screen-header'}
        />

        <ActionStatusBar
          inspectionId={externalId!}
          inspectionCategoryTypeId={undercarriageCategory?.externalId ?? ''}
          dataLevel={ObservableLevelsEnum.equipmentPart}
          entities={[
            {
              entityId: equipmentPart.id,
              entityExternalId: equipmentPart.externalId,
            },
          ]}
          paths={[
            Undercarriage.Shoes.PitchExtension.WearIndicators.Pins.Broken
              .Checked,
            Undercarriage.Shoes.PitchExtension.TapeMeasure
              .CalculatedTapePitchExtension,
            Undercarriage.Shoes.PitchExtension.PitchGauge.CalculatedPitchGauge,
          ]}
          pathsLike={[
            ...wearIndicators
              .map((group) =>
                group.fields.map((field) => field.path + '.Checked'),
              )
              .flat(),
          ]}
        />

        <ScreenObservationLayout>
          <TapeMeasure
            measurementPath={
              Undercarriage.Shoes.PitchExtension.TapeMeasure.Measurement
            }
            shoesPath={
              Undercarriage.Shoes.PitchExtension.TapeMeasure.NumberOfShoes
            }
            extensionPath={
              Undercarriage.Shoes.PitchExtension.TapeMeasure
                .CalculatedTapePitchExtension
            }
            numberOfShoes={numberOfShoes}
            dataLevel={ObservableLevelsEnum.equipmentPart}
            entityId={equipmentPart.id}
            entityExternalId={equipmentPart.externalId}
            inspectionId={externalId!}
            inspectionCategoryTypeId={undercarriageCategory?.externalId ?? ''}
          />

          <PitchGaugeWornLengthComponent
            path={Undercarriage.Shoes.PitchExtension.PitchGauge}
            dataLevel={ObservableLevelsEnum.equipmentPart}
            entityId={equipmentPart.id}
            entityExternalId={equipmentPart.externalId}
            inspectionId={externalId!}
            inspectionCategoryTypeId={undercarriageCategory?.externalId ?? ''}
          />

          {wearIndicators.map((group) => {
            return (
              <div key={group.label}>
                <Label
                  titleType="fieldTitle"
                  className="py-2"
                  tooltipKey={`undercarriage-shoes-pitch-extension-wear-indicators-${MdHelpers.normaliseTooltipKeyComponent(group.label.toLowerCase())}`}
                >
                  {group.label}
                </Label>
                {group.fields.map((field) => (
                  <CheckboxWithComponents
                    label={field.label}
                    key={field.label}
                    path={field.path + '.Checked'}
                    dataLevel={ObservableLevelsEnum.equipmentPart}
                    entityId={equipmentPart.id}
                    entityExternalId={equipmentPart.externalId}
                    inspectionId={externalId!}
                    inspectionCategoryTypeId={
                      undercarriageCategory?.externalId ?? ''
                    }
                    childPaths={[field.path + '.Value']}
                    infoTooltipKey={`undercarriage-shoes-pitch-extension-wearindicators-${MdHelpers.normaliseTooltipKeyComponent(group.label.toLowerCase())}-${MdHelpers.normaliseTooltipKeyComponent(field.label)}`}
                  >
                    <DropdownField
                      options={Array.from({ length: numberOfShoes }, (_, i) =>
                        (i + 1).toString(),
                      )}
                      placeholder="Number of shoes"
                      path={field.path + '.Value'}
                      dataLevel={ObservableLevelsEnum.equipmentPart}
                      entityId={equipmentPart.id}
                      entityExternalId={equipmentPart.externalId}
                      inspectionId={externalId!}
                      inspectionCategoryTypeId={
                        undercarriageCategory?.externalId ?? ''
                      }
                    />
                  </CheckboxWithComponents>
                ))}
              </div>
            );
          })}

          <div className="pt-4"></div>

          <PhotoSelector
            inspectionId={externalId!}
            inspectionCategoryTypeId={undercarriageCategory?.externalId ?? ''}
            dataLevel={ObservableLevelsEnum.equipmentPart}
            title="Photos"
            entityId={equipmentPart.id}
            entityExternalId={equipmentPart.externalId}
            path={Undercarriage.Shoes.PitchExtension.Photo}
          />

          <RichTextField
            inspectionId={externalId!}
            inspectionCategoryTypeId={undercarriageCategory?.externalId ?? ''}
            dataLevel={ObservableLevelsEnum.equipmentPart}
            entityId={equipmentPart.id}
            entityExternalId={equipmentPart.externalId}
            path={Undercarriage.Shoes.PitchExtension.Comments}
            label="Comments"
            placeholder="Enter your comments here..."
            className="pt-4"
          />

          <CompleteButton onClick={() => navigate(-1)} />
        </ScreenObservationLayout>
      </div>
    </ObservationDraftScope>
  );
};
